import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  MapPin, Phone, Clock, Star, QrCode, ArrowLeft,
  Mail, CheckCircle, Package, Globe, ExternalLink, X, ChevronLeft, ChevronRight
} from 'lucide-react';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import { storeService, StoreDetails as StoreDetailsType } from '../../services/user/storeService';
import { toast } from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageHelper';

const StoreDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [store, setStore] = useState<StoreDetailsType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [failedPhotoUrls, setFailedPhotoUrls] = useState<string[]>([]);

  useEffect(() => {
    const fetchStoreDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await storeService.getStoreDetails(id);
        console.log('Store details loaded:', data);
        setStore(data);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching store details:', err);
        setError('Store not found or unavailable');
        if (err?.isNetworkError || err?.isConnectionRefused) {
          toast.error('Unable to connect to server');
        } else {
          toast.error('Failed to load store details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStoreDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh] pt-24">
          <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mb-4"></div>
          <p className="text-secondary-500 font-medium">Loading store details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 pt-24">
          <div className="text-8xl mb-6">🏪</div>
          <h2 className="text-3xl font-bold text-secondary-900 mb-4">Store Not Found</h2>
          <p className="text-secondary-600 mb-6 text-center max-w-md">{error || "The store you're looking for doesn't exist."}</p>
          <Link
            to="/store"
            className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-3 rounded-xl font-semibold transition-all hover:shadow-lg hover:scale-105"
          >
            Back to Stores
          </Link>
        </div>
        <Footer />
      </div>
    );
  }



  const openImagePreview = (imageUrl: string, index: number) => {
    setSelectedImage(imageUrl);
    setCurrentImageIndex(index);
  };

  const closeImagePreview = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (!store?.photos) return;

    const visiblePhotos = store.photos.filter(p => !failedPhotoUrls.includes(p.photo_url));
    if (visiblePhotos.length === 0) return;

    let newIndex = currentImageIndex;
    if (direction === 'prev') {
      newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : visiblePhotos.length - 1;
    } else {
      newIndex = currentImageIndex < visiblePhotos.length - 1 ? currentImageIndex + 1 : 0;
    }

    setCurrentImageIndex(newIndex);
    setSelectedImage(visiblePhotos[newIndex].photo_url);
  };

  // Safe rating extraction
  const ratingValue = typeof store.rating === 'object' ? store.rating.average : store.rating;
  const reviewCount = typeof store.rating === 'object' ? store.rating.total_reviews : 0;

  // Parse hours
  let displayHours = "Not available";
  if (store.status?.is_24_7) {
    displayHours = "Open 24 Hours";
  } else if (store.status?.business_timings) {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const timings = store.status.business_timings;
    if (timings && timings[today]) {
      if (timings[today].is_open) {
        displayHours = `Today: ${timings[today].open_time} - ${timings[today].close_time}`;
      } else {
        displayHours = "Closed Today";
      }
    }
  }

  // Safe address extraction
  const fullAddress = typeof store.address === 'object'
    ? `${store.address.address}, ${store.address.city}, ${store.address.state}, ${store.address.country}`
    : store.address || 'Address not available';

  return (
    <div className="min-h-screen bg-[#faf8ed]">
      <Navbar />

      {/* Header with Search and Stats */}
      <div className="pt-28 pb-8 px-4 bg-white border-b border-[#0c513f]/5 z-30 transition-all duration-300">
        <div className="container-premium">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <button
              onClick={() => navigate(-1)}
              className="group flex items-center gap-2 text-[#0c513f] hover:text-[#0a4535] transition-all bg-[#faf8ed] px-5 py-2.5 rounded-2xl border border-[#0c513f]/10"
            >
              <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
              <span className="font-bold text-sm">Stores</span>
            </button>

            <div className="flex-1 max-w-2xl relative group">
              <div className="absolute left-5 top-1/2 -translate-y-1/2 text-secondary-400 group-focus-within:text-[#0c513f] transition-colors">
                <Package size={20} />
              </div>
              <input 
                type="text" 
                placeholder={`Search in ${store.business_name}...`}
                className="w-full pl-14 pr-6 py-3 bg-[#faf8ed] border-2 border-transparent focus:border-[#0c513f]/20 rounded-2xl outline-none font-medium text-secondary-800 transition-all"
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-[#fff9e6] px-4 py-2 rounded-2xl border border-yellow-100">
                <Star className="text-yellow-500 fill-yellow-500" size={18} />
                <span className="font-black text-[#0c513f]">{typeof ratingValue === 'number' ? ratingValue.toFixed(1) : ratingValue}</span>
              </div>
              {store.has_qr_payment && (
                <button className="bg-[#0c513f] text-white p-2.5 rounded-2xl shadow-lg hover:bg-[#0a4535] transition-all hover:scale-110">
                  <QrCode size={24} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="px-4 pb-20">
        <div className="container-premium">
          <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-[#0c513f]/5">

            {/* Banner Image */}
            <div className="relative h-80 md:h-[400px] bg-mesh-premium flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0 bg-[#0c513f]/20 z-0"></div>
              {getImageUrl(store.banner_image) ? (
                <>
                  <img
                    src={getImageUrl(store.banner_image) || ''}
                    alt={store.business_name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback if image fails to load
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        const fallback = parent.querySelector('.fallback-banner-container');
                        if (fallback) (fallback as HTMLElement).style.display = 'flex';
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="fallback-banner-container hidden text-9xl text-white/20 relative z-10 animate-float w-full h-full items-center justify-center">
                    {store.category?.category_icon ? (
                      <img src={getImageUrl(store.category.category_icon) || store.category.category_icon} className="h-40 w-40 object-contain opacity-50 drop-shadow-lg" alt="Category" />
                    ) : '🏢'}
                  </div>
                </>
              ) : (
                <div className="text-9xl text-white/20 relative z-10 animate-float">
                  {store.category?.category_icon ? (
                    <img src={getImageUrl(store.category.category_icon) || store.category.category_icon} className="h-40 w-40 object-contain opacity-50 drop-shadow-lg" alt="Category" />
                  ) : '🏢'}
                </div>
              )}

              {/* Logo overlay */}
              {getImageUrl(store.logo) ? (
                <div className="absolute -bottom-16 left-8 md:left-12 w-32 h-32 md:w-40 md:h-40 rounded-[2rem] border-8 border-white shadow-2xl overflow-hidden bg-white z-20">
                  <img
                    src={getImageUrl(store.logo) || ''}
                    alt="Logo"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        const fallback = parent.querySelector('.fallback-logo-overlay');
                        if (fallback) (fallback as HTMLElement).style.display = 'flex';
                      }
                    }}
                  />
                  <div className="fallback-logo-overlay w-full h-full bg-[#faf8ed] hidden items-center justify-center text-4xl">
                    🏪
                  </div>
                </div>
              ) : null}

              {/* Status Badge */}
              <div className={`absolute top-8 right-8 px-6 py-2.5 rounded-full text-sm font-bold shadow-lg backdrop-blur-md z-20 border border-white/20 ${store.status?.is_open
                ? 'bg-[#0c513f] text-white'
                : 'bg-red-500 text-white'
                }`}>
                {store.status?.is_open ? '● Open Now' : '● Closed'}
              </div>
            </div>

            {/* Business Info Header */}
            <div className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="relative -mt-24 md:-mt-32">
                  <div className="w-32 h-32 md:w-48 md:h-48 rounded-[2.5rem] border-8 border-white shadow-2xl overflow-hidden bg-white">
                    {getImageUrl(store.logo) ? (
                      <>
                        <img
                          src={getImageUrl(store.logo) || ''}
                          alt="Logo"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              const fallback = parent.querySelector('.fallback-main-logo');
                              if (fallback) (fallback as HTMLElement).style.display = 'flex';
                            }
                          }}
                        />
                        <div className="fallback-main-logo w-full h-full bg-[#faf8ed] hidden items-center justify-center text-4xl">
                          🏪
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-[#faf8ed] flex items-center justify-center text-4xl">
                        🏪
                      </div>
                    )}
                  </div>
                  <div className={`absolute -bottom-2 -right-2 p-3 rounded-2xl shadow-lg border-4 border-white ${store.status?.is_open ? 'bg-[#0c513f]' : 'bg-red-500'}`}>
                    <Clock size={20} className="text-white" />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-[#faf8ed] text-[#0c513f] px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider border border-[#0c513f]/10">
                      {store.category?.category_name}
                    </span>
                    {store.country && (
                      <span className="bg-secondary-50 text-secondary-500 px-3 py-1 rounded-full text-xs font-bold">
                        {store.country}
                      </span>
                    )}
                  </div>
                  <h1 className="text-4xl md:text-6xl font-black text-[#0c513f] mb-4 leading-tight tracking-tighter">
                    {store.business_name}
                  </h1>
                  <p className="text-xl text-secondary-600 font-medium max-w-3xl leading-relaxed">
                    {store.description}
                  </p>
                </div>
              </div>
            </div>

              {/* Product Navigation Tabs */}
              <div className="flex overflow-x-auto gap-4 mb-12 pb-4 scrollbar-hide no-scrollbar">
                <button className="px-8 py-3 bg-[#0c513f] text-white rounded-2xl font-bold shadow-lg whitespace-nowrap">
                  All Products
                </button>
                <button className="px-8 py-3 bg-white text-secondary-600 rounded-2xl font-bold hover:bg-secondary-50 transition-all whitespace-nowrap shadow-sm border border-secondary-100">
                  Featured
                </button>
                <button className="px-8 py-3 bg-white text-secondary-600 rounded-2xl font-bold hover:bg-secondary-50 transition-all whitespace-nowrap shadow-sm border border-secondary-100">
                  Reviews
                </button>
                <button className="px-8 py-3 bg-white text-secondary-600 rounded-2xl font-bold hover:bg-secondary-50 transition-all whitespace-nowrap shadow-sm border border-secondary-100">
                  Contact
                </button>
              </div>

              {/* Enhanced Product Grid */}
              <div className="mb-20">
                <h3 className="text-3xl font-black text-[#0c513f] mb-10 flex items-center justify-between">
                  Explore Products
                  <span className="text-sm font-bold text-secondary-400 bg-secondary-50 px-4 py-1.5 rounded-full border border-secondary-100 uppercase tracking-widest">
                    {store.products?.length || 0} ITEMS
                  </span>
                </h3>

                {store.products && store.products.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                    {store.products.map((product, idx) => (
                      <div 
                        key={idx} 
                        className="group bg-white rounded-[2.5rem] p-4 border-2 border-transparent hover:border-[#0c513f]/10 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                      >
                        <div className="relative aspect-square bg-[#faf8ed] rounded-[2rem] overflow-hidden mb-6">
                          {getImageUrl(product.image) ? (
                            <img 
                              src={getImageUrl(product.image) || ''} 
                              alt={product.product_name} 
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-6xl opacity-30">
                              📦
                            </div>
                          )}
                          
                          {/* Discount Badge Mockup */}
                          {idx % 3 === 0 && (
                            <div className="absolute top-4 left-4 bg-red-500 text-white text-xs font-black px-4 py-1.5 rounded-full shadow-lg">
                              -20% OFF
                            </div>
                          )}

                          <button className="absolute bottom-4 right-4 p-4 bg-[#0c513f] text-white rounded-2xl shadow-xl translate-y-20 group-hover:translate-y-0 transition-transform duration-500 hover:bg-[#0a4535] hover:scale-110">
                            <QrCode size={20} />
                          </button>
                        </div>

                        <div className="px-3 pb-2">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="text-xl font-black text-[#0c513f] line-clamp-1 group-hover:text-[#0a4535] transition-colors uppercase tracking-tight">
                              {product.product_name}
                            </h4>
                          </div>
                          
                          <div className="flex items-baseline gap-2 mb-4">
                            <span className="text-2xl font-black text-[#0c513f]">
                              {typeof product.price === 'number' ? `$${product.price.toFixed(2)}` : product.price}
                            </span>
                            {idx % 3 === 0 && (
                              <span className="text-sm text-secondary-300 line-through font-bold">
                                $29.99
                              </span>
                            )}
                          </div>

                          <button className="w-full py-3.5 bg-secondary-50 text-secondary-800 font-black rounded-[1.2rem] hover:bg-[#0c513f] hover:text-white transition-all transition-colors duration-300 flex items-center justify-center gap-2 border border-secondary-100 group-hover:border-transparent uppercase text-xs tracking-widest shadow-sm">
                            Add to Cart
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-[#faf8ed]/50 rounded-[3rem] p-20 text-center border-2 border-dashed border-[#0c513f]/10">
                    <Package className="mx-auto text-[#0c513f]/20 mb-6" size={80} />
                    <p className="text-2xl text-secondary-800 font-bold mb-2">Inventory Arriving Soon</p>
                    <p className="text-secondary-500 font-medium">This store hasn't listed their products yet.</p>
                  </div>
                )}
              </div>

              {/* Store Information Grid */}
              <div className="grid lg:grid-cols-3 gap-8 mb-16">
                {/* Contact Section */}
                <div className="lg:col-span-2 bg-white p-8 md:p-12 rounded-[3.5rem] shadow-sm border border-secondary-100">
                  <h3 className="text-3xl font-black text-[#0c513f] mb-10 tracking-tight">Store Details</h3>
                  
                  <div className="grid md:grid-cols-2 gap-10">
                    <div className="space-y-8">
                      <div className="flex gap-5">
                        <div className="p-4 bg-[#faf8ed] rounded-2xl text-[#0c513f] flex-shrink-0">
                          <MapPin size={28} />
                        </div>
                        <div>
                          <p className="text-xs font-black text-secondary-300 uppercase tracking-widest mb-1">Official Address</p>
                          <p className="text-lg font-bold text-secondary-800 leading-tight">{fullAddress}</p>
                        </div>
                      </div>

                      <div className="flex gap-5">
                        <div className="p-4 bg-[#faf8ed] rounded-2xl text-[#0c513f] flex-shrink-0">
                          <Clock size={28} />
                        </div>
                        <div>
                          <p className="text-xs font-black text-secondary-300 uppercase tracking-widest mb-1">Operating Hours</p>
                          <p className="text-lg font-bold text-secondary-800 leading-tight">{displayHours}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-8">
                      <div className="flex gap-5">
                        <div className="p-4 bg-[#faf8ed] rounded-2xl text-[#0c513f] flex-shrink-0">
                          <Phone size={28} />
                        </div>
                        <div>
                          <p className="text-xs font-black text-secondary-300 uppercase tracking-widest mb-1">Support Line</p>
                          <p className="text-lg font-bold text-secondary-800 leading-tight">{store.contact?.phone || 'Not provided'}</p>
                        </div>
                      </div>

                      <div className="flex gap-5">
                        <div className="p-4 bg-[#faf8ed] rounded-2xl text-[#0c513f] flex-shrink-0">
                          <Mail size={28} />
                        </div>
                        <div>
                          <p className="text-xs font-black text-secondary-300 uppercase tracking-widest mb-1">Email Support</p>
                          <p className="text-lg font-bold text-secondary-800 leading-tight break-all">{store.contact?.email || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* QR/Payment Sidebar */}
                <div className="bg-[#0c513f] text-white p-10 rounded-[3.5rem] shadow-2xl relative overflow-hidden flex flex-col items-center justify-center text-center">
                  <div className="absolute inset-0 bg-grid-white/5 opacity-20"></div>
                  <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full blur-[60px] -mr-20 -mt-20"></div>
                  
                  <div className="bg-white p-6 rounded-[2.5rem] mb-8 shadow-2xl relative z-10 transform hover:scale-105 transition-transform duration-500">
                    <QrCode size={120} className="text-[#0c513f]" />
                  </div>
                  
                  <h3 className="text-2xl font-black mb-4 relative z-10">Scan to Pay</h3>
                  <p className="text-white/70 font-medium mb-8 relative z-10">
                    Pay instantly at the counter or for home delivery using any banking app.
                  </p>
                  
                  <button className="w-full py-4 bg-white text-[#0c513f] rounded-2xl font-black text-lg shadow-xl hover:bg-secondary-50 transition-all active:scale-95 uppercase tracking-widest relative z-10">
                    Download Receipt
                  </button>
                </div>
              </div>

              {/* Photo Gallery */}
              {store.photos && store.photos.filter(photo => !failedPhotoUrls.includes(photo.photo_url)).length > 0 && (
                <div className="mb-16">
                  <h3 className="text-3xl font-black text-[#0c513f] mb-8 tracking-tight">Photo Gallery</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {store.photos
                      .filter(photo => !failedPhotoUrls.includes(photo.photo_url))
                      .map((photo, i) => (
                        <div
                          key={i}
                          onClick={() => openImagePreview(photo.photo_url, i)}
                          className="aspect-square rounded-[2rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all hover:scale-105 cursor-pointer group relative"
                        >
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10"></div>
                          <img
                            src={getImageUrl(photo.photo_url) || photo.photo_url}
                            alt={`Store photo ${i + 1}`}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            onError={() => {
                              setFailedPhotoUrls(prev => [...prev, photo.photo_url]);
                            }}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Reviews Section */}
              <div className="border-t border-[#0c513f]/10 pt-12">
                <h3 className="heading-medium text-[#0c513f] mb-10 flex items-center gap-4">
                  <div className="bg-[#fff9e6] p-3 rounded-2xl">
                    <Star className="text-yellow-500 fill-yellow-500" size={32} />
                  </div>
                  Customer Reviews
                </h3>

                {store.recent_reviews && store.recent_reviews.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-6">
                    {store.recent_reviews.map((review, idx) => (
                      <div key={idx} className="bg-[#faf8ed] rounded-[2rem] p-8 border border-[#0c513f]/5 hover:shadow-lg transition-all">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-[#0c513f] flex items-center justify-center text-white font-black text-xl shadow-md">
                              {(review.user_name || "U")[0].toUpperCase()}
                            </div>
                            <span className="font-bold text-[#0c513f] text-lg">{review.user_name || "Anonymous"}</span>
                          </div>
                          <div className="flex items-center gap-1 bg-[#fff9e6] px-4 py-1.5 rounded-full border border-yellow-100">
                            <Star className="text-yellow-500 fill-yellow-500" size={16} />
                            <span className="text-base font-black text-[#0c513f]">{review.rating}</span>
                          </div>
                        </div>
                        <p className="text-secondary-700 text-lg leading-relaxed mb-4 italic font-medium">"{review.comment}"</p>
                        <p className="text-sm text-secondary-400 font-bold uppercase tracking-wider">
                          {new Date(review.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-[#fff9e6] rounded-[2.5rem] p-12 text-center border-2 border-yellow-100/50">
                    <Star className="text-yellow-500 fill-yellow-500 mx-auto mb-6 opacity-50" size={64} />
                    <p className="text-[#0c513f] text-2xl mb-4 font-bold">
                      <span className="font-black text-4xl block mb-2">
                        {typeof ratingValue === 'number' ? ratingValue.toFixed(1) : ratingValue}
                      </span>
                      out of 5 stars
                    </p>
                    <p className="text-secondary-600 font-medium text-lg">
                      Be the first to review this business!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 animate-fade-in"
          onClick={closeImagePreview}
        >
          {/* Close Button */}
          <button
            onClick={closeImagePreview}
            className="absolute top-4 right-4 z-10 p-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-all hover:scale-110"
            aria-label="Close preview"
          >
            <X className="text-white" size={28} />
          </button>

          {/* Navigation Buttons */}
          {store?.photos && store.photos.filter(photo => !failedPhotoUrls.includes(photo.photo_url)).length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage('prev');
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-all hover:scale-110"
                aria-label="Previous image"
              >
                <ChevronLeft className="text-white" size={32} />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage('next');
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-all hover:scale-110"
                aria-label="Next image"
              >
                <ChevronRight className="text-white" size={32} />
              </button>
            </>
          )}

          {/* Image */}
          <div
            className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage}
              alt="Preview"
              className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl animate-scale-in"
            />
          </div>

          {/* Image Counter */}
          {store?.photos && store.photos.filter(photo => !failedPhotoUrls.includes(photo.photo_url)).length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full">
              <p className="text-white font-semibold">
                {currentImageIndex + 1} / {store.photos.filter(photo => !failedPhotoUrls.includes(photo.photo_url)).length}
              </p>
            </div>
          )}
        </div>
      )}

      <Footer />
    </div>
  );
};

export default StoreDetails;
